import { useContext, useMemo } from 'react'

import { useHealthToolsCache } from '@hhgtech/hhg-components'
import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Box, rem, Stack } from '@mantine/core'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { Container } from 'components/atoms/container'
import Warning from 'components/atoms/icons/Warning'
import { AdBlock } from 'components/molecules/adBlock'
import { AdBlockContainer } from 'components/molecules/adBlock/adBlockContainer'
import { FixedBottomAdBlock } from 'components/molecules/adBlock/deferFixedBottomAdBlock'
import { BmiForm, BmiFormHelpers } from 'components/molecules/bmiForm'
import { FaqAccordion } from 'components/molecules/faqAccordion'
import { HealthToolBreadcrumb } from 'components/molecules/healthToolBreadcrumb'
import { ToolsBannerCardV2 } from 'components/molecules/toolsBannerCardV2'
import { ToolsInfoCard } from 'components/molecules/toolsInfoCard'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import {
  StyledHealthTools,
  StyledSuggestedArticlesContainer,
} from 'components/pages/healthTools/index.styled'
import { HealthToolLayout } from 'components/templates/healthToolLayout'
import {
  getDefaultLanguageOptions,
  NavigatorLayout,
} from 'components/templates/navigatorLayout'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath } from 'interfaces/constants'
import {
  HEALTH_TOOL_NAME,
  HEALTH_TOOL_TYPE,
} from 'interfaces/constants/healthTools'
import { FooterInfo, ToolContent } from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { getAdTag, getCountryAdTag, getLocaleCategoryAdTag } from 'utils/ads'

import {
  addMoreDataBMIAndSendToSidis,
  BMI_SIDIS_KEY,
  generateBmiRedirect,
} from './helpers'
import { HEALTH_TOOL_COMMON_CONFIGS, ToolPageType } from '../helpers'

export type InputError = {
  age: string
  weight: string
  height: string
}

export type BMIToolPageProps = {
  type: ToolPageType
  isMobileSsr: boolean
  bmiContent: ToolContent
  pageType?: string
  userInfo?: any
  footerInfo?: FooterInfo
  dataNavMenuContent?: NavMenuContentType
  bmiContentWParams: ToolContent
}

const BMIToolPage = ({
  bmiContent,
  footerInfo,
  dataNavMenuContent,
  bmiContentWParams,
}: BMIToolPageProps) => {
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const { locale, router, asPath } = useRouterExtend()
  const { formatMessage: f } = useIntl()

  const isMobile = useIsMobile()

  const { getHealthToolLink } = useHealthToolsCache(locale)
  const bmiLink = useMemo(() => getHealthToolLink(HEALTH_TOOL_TYPE.BMI), [
    getHealthToolLink,
  ])

  const isEnglishTool = router.query.slug?.[0] === 'bmi-calculator-en'
  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: isEnglishTool
      ? {
          result: 'HealthTools/BMICalculatorEn',
          redundant: true,
        }
      : getLocaleCategoryAdTag(locale, asPath, ['health-tools/bmi']) || {
          result: 'HealthTools',
          redundant: true,
        },
  })

  const onSubmit = async (data: BmiFormHelpers.BmiFormValue) => {
    const dataToSidis = addMoreDataBMIAndSendToSidis(data, {
      positionType: 'landing_page',
      isMobile,
    })
    localStorage.setItem(BMI_SIDIS_KEY, JSON.stringify(dataToSidis))

    const urlRedirect = generateBmiRedirect(
      window.location.origin + bmiLink,
      data,
    )

    // Redirect to SSO if user not login
    // Force userId every submit
    // TODO: don't know this. copy from previous logic
    if (!isLoggedIn && !isEnglishTool) {
      redirectToSSOWithReturnUrl(urlRedirect, 'bmi')
    } else {
      router.push(urlRedirect)
    }
  }

  const pageItem = [
    {
      label: f({ id: 'healthtools.title' }),
      value: `/health-tools`,
    },
    {
      label: f({
        id: 'bmi.heading',
        defaultMessage: 'BMI Calculator',
      }),
      value: '#',
    },
  ]

  const enabledAd = !!bmiContent.enable_ad

  return (
    <>
      <NavigatorLayout
        title={f({ id: 'bmiPage.seoTitle', defaultMessage: 'BMI Tool' })}
        description={f({
          id: 'bmiPage.seoDescription',
          defaultMessage: 'BMI Tool',
        })}
        image={`${getHelloSitesUrl(locale)}${assetImagePath}/bmiThumbnail.png`}
        SEOData={{
          schemaData: f({
            id: 'bmiPage.seoSchema',
            defaultMessage: 'BMI Tool',
          }),
          apiMetaData: bmiContentWParams?.metaTag || bmiContent?.metaTag,
          isIndexable: true,
        }}
        footerInfo={footerInfo}
        isSponsored={bmiContent?.is_sponsored}
        sponsoredType="content"
        sponsoredBy={bmiContent.sponsor || undefined}
        dataNavMenuContent={dataNavMenuContent}
        languageOptions={getDefaultLanguageOptions(locale, router.asPath)}
        activeMenuService="tools"
        isHealthToolResultSharing
      >
        <StyledHealthTools>
          <Box
            sx={(theme) => ({
              backgroundColor:
                theme.colors[
                  HEALTH_TOOL_COMMON_CONFIGS[HEALTH_TOOL_NAME.BMI]
                ][1],
              paddingBottom: 24,
            })}
          >
            <ToolsBannerCardV2
              breadcrumbProps={{ pageItem }}
              icon={
                <img
                  alt="health-tool-icon"
                  loading="lazy"
                  src={
                    bmiContent?.featured_image || `${assetImagePath}/bmi.svg`
                  }
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              }
              heading={
                bmiContent.title ||
                f({
                  id: 'bmi.heading',
                  defaultMessage: 'BMI Calculator',
                })
              }
              description={
                bmiContent.description ||
                f({
                  id: 'bmi.description',
                  defaultMessage: 'BMI Calculator',
                })
              }
              reviewBy={bmiContent.reviewBy}
              isMobile={isMobile}
              styles={(theme) => ({
                root: { backgroundColor: 'transparent' },
                inner: {
                  [theme.fn.largerThan('sm')]: {
                    paddingBottom: 8,
                  },
                },
                thumbnailCol: {
                  [theme.fn.largerThan('sm')]: {
                    right: 24,
                  },
                },
                thumbnailWrapper: {
                  width: 32,
                },
                thumbnailInner: {
                  [theme.fn.largerThan('sm')]: {
                    width: 66,
                    height: 66,
                    backgroundColor: 'rgba(255,255,255,0.3)',
                  },
                  borderRadius: '100%',
                  boxShadow: '0px 3.9px 19.51px 0px #00B16A3D',
                },
                thumbnail: {
                  [theme.fn.smallerThan('sm')]: {
                    display: 'none',
                  },
                  [theme.fn.largerThan('sm')]: {
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '100%',
                    boxShadow: '0px 3.9px 19.51px 0px #189EFF66',
                  },
                },
                medicalReview: {
                  [theme.fn.smallerThan('sm')]: {
                    paddingRight: 0,
                  },
                },
              })}
            />
            <Container>
              <Box
                sx={(theme) => ({
                  [theme.fn.smallerThan('sm')]: {
                    marginLeft: -8,
                    marginRight: -8,
                  },
                  [theme.fn.largerThan('sm')]: {
                    paddingLeft: 24,
                    paddingRight: 24,
                  },
                })}
              >
                {bmiLink && <BmiForm onSubmit={onSubmit} template="page" />}
              </Box>
            </Container>
          </Box>
          <HealthToolLayout>
            <HealthToolLayout.LeftColumn>
              {enabledAd && isMobile && (
                <AdBlock
                  adUnitPath={adUnitPath}
                  className="top-ad"
                  size={[300, 250]}
                  divId={'div-gpt-ad-topbanner-mobile'}
                  pos={['TopBanner']}
                />
              )}
              {bmiContent.disclaimer && (
                <ToolsInfoCard
                  headingTitle={f({ id: 'tools.disclaimer' })}
                  icon={<Warning size={18} />}
                >
                  <Stack spacing={rem(16)}>
                    <Text size="p2">{bmiContent.disclaimer}</Text>
                  </Stack>
                </ToolsInfoCard>
              )}
              {bmiContent.information?.length > 0 && (
                <FaqAccordion
                  faqs={bmiContent.information}
                  titleProps={{
                    icon: (
                      <img
                        style={{
                          height: '16px',
                          width: '16px',
                        }}
                        src={`${assetImagePath}/info-icon.svg`}
                        alt=""
                        loading="lazy"
                      />
                    ),
                    headingTitle: f({
                      id: 'tools.info',
                      defaultMessage: 'Info',
                    }),
                  }}
                />
              )}
            </HealthToolLayout.LeftColumn>
            <HealthToolLayout.RightColumn>
              {!isMobile ? (
                <StyledSuggestedArticlesContainer>
                  <div className="top-ad">
                    {enabledAd && (
                      <AdBlock
                        adUnitPath={adUnitPath}
                        size={[
                          [300, 250],
                          [300, 600],
                        ]}
                        divId={'div-gpt-ad-topbanner-desktop'}
                        pos={['TopBanner']}
                      />
                    )}
                  </div>

                  <div className="bottom-ad"></div>
                </StyledSuggestedArticlesContainer>
              ) : undefined}
            </HealthToolLayout.RightColumn>
          </HealthToolLayout>
          {enabledAd && (
            <AdBlockContainer>
              <Container>
                <FixedBottomAdBlock
                  adUnitPath={adUnitPath}
                  className="article-bottom-ad"
                  isMobile={isMobile}
                />
              </Container>
            </AdBlockContainer>
          )}
          {isMobile && <HealthToolBreadcrumb pageItem={pageItem} />}
        </StyledHealthTools>
      </NavigatorLayout>
      <MobileBottomNavigationWrapper.FitnessHealthTool />
    </>
  )
}

export default BMIToolPage
